import { z } from 'zod';

import { SortDirectionType } from '#core/schema/defs.ts';

/**
 * Column configuration for LLM-controlled table layout.
 * Specifies which columns to display, their order, and custom headers.
 */
export const ColumnConfigSchema = z.object({
  accessor: z.string().describe('Column key/property name from the data, supports dot notation for nested paths'),
  title: z.string().optional().describe('Custom display header (defaults to accessor name if omitted)'),
  width: z.number().optional().describe('Fixed column width in pixels'),
  decimalScale: z
    .number()
    .optional()
    .describe('Number of decimal places for numeric columns. Auto-detected from data magnitude if omitted.'),
});

export type ColumnConfig = z.infer<typeof ColumnConfigSchema>;

export type SortDirection = z.infer<typeof SortDirectionType>;

/**
 * Data shape for pure table rendering.
 * Accepts pre-transformed records - no data fetching or resolution.
 *
 * Used by both dashboard TableComponent and chat TableBlock.
 *
 * Note: Uses z.unknown() for record values for runtime flexibility.
 * transformToTaggedObjects() replaces empty additionalProperties with primitives
 * during JSON Schema generation for API compatibility.
 */
export const TableDataSchema = z.object({
  records: z.array(z.record(z.string(), z.unknown())).describe('Array of row objects to display'),
  columns: z
    .array(ColumnConfigSchema)
    .optional()
    .describe('Column configuration. If omitted, columns are auto-detected from first record'),
  pinnedColumns: z
    .array(z.string())
    .optional()
    .describe('Column accessors to pin (freeze) on the left side of the table'),
  title: z.string().optional().describe('Optional table title displayed above the table'),

  // Selection
  selectionEnabled: z.boolean().optional().describe('Enable row selection with checkboxes'),

  // Expansion
  expansionEnabled: z.boolean().optional().describe('Enable row expansion with detail panels'),

  // Sorting
  sortingEnabled: z.boolean().optional().describe('Enable column header sorting'),
  defaultSortColumn: z.string().optional().describe('Column accessor for initial sort'),
  defaultSortDirection: SortDirectionType.optional().describe('Initial sort direction'),
});

export type TableData = z.infer<typeof TableDataSchema>;

/**
 * Unified table component schema for LLM and dashboard use.
 *
 * Strict schema - all props must be explicitly defined.
 * Records is optional; the host may inject row data after parsing
 * (e.g. plugins resolving data from an upstream dataset reference).
 *
 * Note: Uses z.unknown() for record values for runtime flexibility.
 * The transformToTaggedObjects() function replaces empty additionalProperties
 * with primitives during JSON Schema generation for API compatibility.
 */
export const TableSchema = z.object({
  type: z.literal('table').describe('Table component type'),
  records: z
    .array(z.record(z.string(), z.unknown()))
    .optional()
    .describe('Row data. Omit when records will be injected by the host.'),
  columns: z.array(ColumnConfigSchema).optional().describe('Column configuration. Auto-detected if omitted.'),
  pinnedColumns: z.array(z.string()).optional().describe('Columns to pin/freeze on the left'),
  title: z.string().optional().describe('Optional table title'),
});

/** @public Input type for TableSchema - use for component props */
export type TableSchemaInput = z.input<typeof TableSchema>;

/** @public Output type for TableSchema - use after Zod parsing */
export type TableSchemaOutput = z.output<typeof TableSchema>;
